import React, { useState } from 'react';
import { GoalsTabProps, Goal, GoalStep, Task } from './types';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Goal Card Component
const SortableGoalCard: React.FC<{ id: number; children: React.ReactNode }> = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'none',
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    touchAction: 'none', // Критично для мобильного драга!
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners}
      className="draggable-item"
    >
      {children}
    </div>
  );
};

const GoalsTab: React.FC<GoalsTabProps> = ({
  goals,
  tasks,
  categories,
  showGoalForm,
  setShowGoalForm,
  showCategoryModal,
  setShowCategoryModal,
  editingGoalId,
  setEditingGoalId,
  filterGoalStatus,
  setFilterGoalStatus,
  goalForm,
  setGoalForm,
  handleCreateGoal,
  handleEditGoal,
  handleCancelGoalEdit,
  handleDeleteGoal,
  handleCompleteGoal,
  handleActivateGoal,
  handleReorderGoals,
  loadData,
  selectedGoalForSteps,
  setSelectedGoalForSteps,
  stepForm,
  setStepForm,
  handleAddStep,
  handleToggleStep,
  handleDeleteStep,
  handleMoveStepUp,
  handleMoveStepDown,
  expandedStepOnCard,
  setExpandedStepOnCard,
  handleAttachTaskToStep,
  handleDetachTaskFromStep,
  categoryForm,
  setCategoryForm,
  handleCreateCategory,
  handleDeleteCategory,
  handleUpdateCategoryOrder
}) => {
  const [localGoals, setLocalGoals] = useState<Goal[]>(goals);

  // Синхронизируем localGoals с goals из пропсов
  React.useEffect(() => {
    setLocalGoals(goals);
  }, [goals]);

  const sensors = useSensors(
    // Для мышки - начинать драг после движения на 8px
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    // Для тача - начинать драг после удержания 200ms
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localGoals.findIndex(g => g.id === active.id);
    const newIndex = localGoals.findIndex(g => g.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedGoals = arrayMove(localGoals, oldIndex, newIndex);
      setLocalGoals(reorderedGoals);

      // Отправляем новый порядок на сервер
      const goalIds = reorderedGoals.map(g => g.id);
      await handleReorderGoals(goalIds);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div>
      {/* Кнопки управления и фильтры */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <button
            onClick={() => setShowGoalForm(!showGoalForm)}
            style={{
              padding: '10px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {showGoalForm ? 'Отмена' : '+ Создать цель'}
          </button>
          <button
            onClick={() => setShowCategoryModal(true)}
            style={{
              padding: '10px 20px',
              background: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            📁 Категории ({categories.length})
          </button>
        </div>

        {/* Фильтр статуса целей */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setFilterGoalStatus('all')}
            style={{
              padding: '8px 16px',
              background: filterGoalStatus === 'all' ? '#3b82f6' : 'white',
              color: filterGoalStatus === 'all' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: filterGoalStatus === 'all' ? 'bold' : 'normal'
            }}
          >
            Все цели
          </button>
          <button
            onClick={() => setFilterGoalStatus('active')}
            style={{
              padding: '8px 16px',
              background: filterGoalStatus === 'active' ? '#3b82f6' : 'white',
              color: filterGoalStatus === 'active' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: filterGoalStatus === 'active' ? 'bold' : 'normal'
            }}
          >
            ⚡ Активные
          </button>
          <button
            onClick={() => setFilterGoalStatus('completed')}
            style={{
              padding: '8px 16px',
              background: filterGoalStatus === 'completed' ? '#3b82f6' : 'white',
              color: filterGoalStatus === 'completed' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: filterGoalStatus === 'completed' ? 'bold' : 'normal'
            }}
          >
            ✅ Завершённые
          </button>
        </div>
      </div>

      {/* Форма создания/редактирования цели */}
      {showGoalForm && (
        <form onSubmit={handleCreateGoal} style={{
          background: '#f9fafb',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0 }}>{editingGoalId ? 'Редактировать цель' : 'Новая цель'}</h3>
          <input
            type="text"
            placeholder="Название цели"
            value={goalForm.name}
            onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
            required
            style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          />
          <textarea
            placeholder="Описание (опционально)"
            value={goalForm.description}
            onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
            rows={2}
            style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px', resize: 'vertical' }}
          />
          
          {/* Выбор категории */}
          <select
            value={goalForm.categoryId || ''}
            onChange={(e) => setGoalForm({ ...goalForm, categoryId: e.target.value ? Number(e.target.value) : null })}
            style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          >
            <option value="">Без категории</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
          
          {categories.length === 0 && !editingGoalId && (
            <div style={{
              padding: '10px',
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '4px',
              marginBottom: '10px',
              fontSize: '13px'
            }}>
              💡 <strong>Подсказка:</strong> Создайте категории (Личное развитие, Семья, Бизнес, Общество, Хобби) для лучшей организации целей
            </div>
          )}
          
          <input
            type="date"
            value={goalForm.dueDate}
            onChange={(e) => setGoalForm({ ...goalForm, dueDate: e.target.value })}
            style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          />
          
          {/* Приоритетная цель */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: goalForm.isPriority ? '#fef3c7' : '#f3f4f6',
            border: goalForm.isPriority ? '2px solid #f59e0b' : '1px solid #d1d5db',
            borderRadius: '6px',
            marginBottom: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}>
            <input
              type="checkbox"
              checked={goalForm.isPriority}
              onChange={(e) => setGoalForm({ ...goalForm, isPriority: e.target.checked })}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ flex: 1, fontWeight: goalForm.isPriority ? 'bold' : 'normal' }}>
              ⭐ Приоритетная цель
            </span>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Дела из этой цели планируются в первую очередь
            </span>
          </label>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" style={{
              flex: 1,
              padding: '10px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}>
              {editingGoalId ? 'Сохранить изменения' : 'Создать цель'}
            </button>
            
            {editingGoalId && (
              <button
                type="button"
                onClick={handleCancelGoalEdit}
                style={{
                  padding: '10px 20px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Отмена
              </button>
            )}
          </div>
        </form>
      )}

      {/* Список целей */}
      <SortableContext
        items={localGoals
          .filter(goal => {
            if (filterGoalStatus === 'all') return true;
            if (filterGoalStatus === 'active') return goal.status === 'active';
            if (filterGoalStatus === 'completed') return goal.status === 'completed';
            return true;
          })
          .map(g => g.id)}
        strategy={rectSortingStrategy}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
          {localGoals
            .filter(goal => {
              // Фильтруем по статусу
              if (filterGoalStatus === 'all') return true;
              if (filterGoalStatus === 'active') return goal.status === 'active';
              if (filterGoalStatus === 'completed') return goal.status === 'completed';
              return true;
            })
            .map(goal => (
            <SortableGoalCard key={goal.id} id={goal.id}>
              <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
            border: goal.category ? `2px solid ${goal.category.color}` : 'none',
            borderRadius: '12px',
            padding: '24px',
            position: 'relative',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s'
          }}>
            {/* Если цель редактируется - показываем форму */}
            {editingGoalId === goal.id ? (
              <form onSubmit={handleCreateGoal} style={{ width: '100%' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Редактировать цель</h3>
                
                <input
                  type="text"
                  placeholder="Название цели"
                  value={goalForm.name}
                  onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                  required
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box' }}
                />
                
                <textarea
                  placeholder="Описание (опционально)"
                  value={goalForm.description}
                  onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                  rows={3}
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px', resize: 'vertical', boxSizing: 'border-box' }}
                />
                
                <select
                  value={goalForm.categoryId || ''}
                  onChange={(e) => setGoalForm({ ...goalForm, categoryId: e.target.value ? Number(e.target.value) : null })}
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box' }}
                >
                  <option value="">Без категории</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
                
                <input
                  type="date"
                  value={goalForm.dueDate}
                  onChange={(e) => setGoalForm({ ...goalForm, dueDate: e.target.value })}
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box' }}
                />
                
                {/* Приоритетная цель */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px',
                  background: goalForm.isPriority ? '#fef3c7' : '#f3f4f6',
                  border: goalForm.isPriority ? '2px solid #f59e0b' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  marginBottom: '15px',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}>
                  <input
                    type="checkbox"
                    checked={goalForm.isPriority}
                    onChange={(e) => setGoalForm({ ...goalForm, isPriority: e.target.checked })}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: goalForm.isPriority ? 'bold' : 'normal', fontSize: '13px' }}>
                    ⭐ Приоритетная цель
                  </span>
                </label>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="submit" style={{
                    flex: 1,
                    padding: '10px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}>
                    Сохранить
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleCancelGoalEdit}
                    style={{
                      padding: '10px 20px',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Отмена
                  </button>
                </div>
              </form>
            ) : (
              <>
                {/* Категория */}
                {goal.category && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    padding: '4px 8px',
                    background: goal.category.color,
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 'bold'
                  }}>
                    {goal.category.icon} {goal.category.name}
                  </div>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px', paddingRight: goal.category ? '140px' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    {goal.isPriority && <span style={{ fontSize: '20px' }} title="Приоритетная цель">⭐</span>}
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>{goal.name}</h3>
                  </div>
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                background: goal.status === 'active' ? '#dbeafe' : goal.status === 'completed' ? '#d1fae5' : '#f3f4f6',
                color: goal.status === 'active' ? '#1e40af' : goal.status === 'completed' ? '#065f46' : '#6b7280'
              }}>
                {goal.status === 'active' ? '🎯 Активна' : goal.status === 'completed' ? '✅ Завершена' : '📦 Архив'}
              </span>
            </div>
            
            {goal.description && (
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '15px' }}>{goal.description}</p>
            )}

            {goal.dueDate && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '15px' }}>
                📅 До: {new Date(goal.dueDate).toLocaleDateString('ru-RU')}
              </div>
            )}

            {/* Прогресс */}
            <div style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#4b5563' }}>Прогресс</span>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#4b5563' }}>{goal.progress || 0}%</span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                background: '#e5e7eb',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${goal.progress || 0}%`,
                  height: '100%',
                  background: goal.progress === 100 ? '#10b981' : '#3b82f6',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>

            {/* Кнопки управления */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '15px', flexWrap: 'wrap' }}>
              <button
                onClick={() => handleEditGoal(goal)}
                style={{
                  flex: '1 1 calc(50% - 4px)',
                  padding: '8px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#4b5563',
                  fontWeight: '500'
                }}
              >
                ✏️ Редактировать
              </button>
              <button
                onClick={() => setSelectedGoalForSteps(goal)}
                style={{
                  flex: '1 1 calc(50% - 4px)',
                  padding: '8px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#4b5563',
                  fontWeight: '500'
                }}
              >
                📋 Шаги
              </button>
              {goal.status === 'active' && (
                <button
                  onClick={() => handleCompleteGoal(goal.id, goal.name)}
                  style={{
                    flex: '1 1 calc(50% - 4px)',
                    padding: '8px',
                    background: '#10b981',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: 'white',
                    fontWeight: '500'
                  }}
                >
                  ✅ Завершить
                </button>
              )}
              {goal.status === 'completed' && (
                <button
                  onClick={() => handleActivateGoal(goal.id)}
                  style={{
                    flex: '1 1 calc(50% - 4px)',
                    padding: '8px',
                    background: '#3b82f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: 'white',
                    fontWeight: '500'
                  }}
                >
                  🔄 Возобновить
                </button>
              )}
              <button
                onClick={() => {
                  if (window.confirm(`Вы уверены, что хотите удалить цель "${goal.name}"? Это действие нельзя отменить.`)) {
                    handleDeleteGoal(goal.id);
                  }
                }}
                style={{
                  flex: '1 1 calc(50% - 4px)',
                  padding: '8px',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#dc2626',
                  fontWeight: '500'
                }}
              >
                🗑️ Удалить
              </button>
            </div>
              </>
            )}
          </div>
            </SortableGoalCard>
          ))}
        </div>
      </SortableContext>

      {localGoals.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          Нет целей. Создайте первую цель нажав "+ Цель"
        </div>
      )}

      {/* Модальное окно управления шагами */}
      {selectedGoalForSteps && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setSelectedGoalForSteps(null)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '24px' }}>🎯 {selectedGoalForSteps.name}</h2>
              <button
                onClick={() => setSelectedGoalForSteps(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            {/* Прогресс */}
            <div style={{ marginBottom: '25px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#4b5563' }}>Прогресс выполнения</span>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#4b5563' }}>{selectedGoalForSteps.progress || 0}%</span>
              </div>
              <div style={{
                width: '100%',
                height: '12px',
                background: '#e5e7eb',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${selectedGoalForSteps.progress || 0}%`,
                  height: '100%',
                  background: selectedGoalForSteps.progress === 100 ? '#10b981' : '#3b82f6',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>

            {/* Форма добавления шага */}
            <form onSubmit={handleAddStep} style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="Название шага"
                  value={stepForm.name}
                  onChange={(e) => setStepForm({ ...stepForm, name: e.target.value })}
                  required
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  + Добавить
                </button>
              </div>
            </form>

            {/* Список шагов */}
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '15px', color: '#374151' }}>
                Шаги ({selectedGoalForSteps.steps?.filter(s => s.completed).length || 0}/{selectedGoalForSteps.steps?.length || 0})
              </h3>

              {selectedGoalForSteps.steps && selectedGoalForSteps.steps.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {selectedGoalForSteps.steps.map((step: GoalStep, index: number) => (
                    <div key={step.id} style={{
                      padding: '15px',
                      background: step.completed ? '#f0fdf4' : '#f9fafb',
                      border: `2px solid ${step.completed ? '#10b981' : '#e5e7eb'}`,
                      borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="checkbox"
                          checked={step.completed}
                          onChange={() => handleToggleStep(step.id, step.completed)}
                          style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                        <span style={{
                          flex: 1,
                          fontSize: '14px',
                          fontWeight: '500',
                          color: step.completed ? '#10b981' : '#374151',
                          textDecoration: step.completed ? 'line-through' : 'none'
                        }}>
                          {step.name}
                        </span>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          {index > 0 && (
                            <button
                              onClick={() => handleMoveStepUp(selectedGoalForSteps.id, step.id, index)}
                              style={{
                                padding: '4px 8px',
                                background: '#e5e7eb',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              ↑
                            </button>
                          )}
                          {index < selectedGoalForSteps.steps!.length - 1 && (
                            <button
                              onClick={() => handleMoveStepDown(selectedGoalForSteps.id, step.id, index)}
                              style={{
                                padding: '4px 8px',
                                background: '#e5e7eb',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              ↓
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteStep(step.id)}
                            style={{
                              padding: '4px 8px',
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      {/* Привязанные задачи к шагу */}
                      {step.tasks && step.tasks.filter((t: Task) => 
                        t.goalId === selectedGoalForSteps?.id
                      ).length > 0 && (
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Задачи:</div>
                          {step.tasks.filter((t: Task) => 
                            t.goalId === selectedGoalForSteps?.id
                          ).map((task: Task) => (
                            <div key={task.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '6px 10px',
                              background: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '4px',
                              marginBottom: '5px'
                            }}>
                              <span style={{ flex: 1, fontSize: '13px', color: '#374151' }}>{task.name}</span>
                              <button
                                onClick={() => handleDetachTaskFromStep(task.id)}
                                style={{
                                  padding: '2px 6px',
                                  background: '#fee2e2',
                                  color: '#dc2626',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                Открепить
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Кнопка привязки задач */}
                      {expandedStepOnCard === step.id ? (
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Выберите дело для привязки к этой цели:</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '150px', overflowY: 'auto' }}>
                            {tasks.filter(t => t.goalId === selectedGoalForSteps?.id && !t.stepId).length === 0 ? (
                              <div style={{ padding: '12px', fontSize: '13px', color: '#9ca3af', textAlign: 'center', fontStyle: 'italic' }}>
                                Нет доступных дел для привязки.<br/>Создайте дело с этой целью во вкладке "Дела".
                              </div>
                            ) : (
                              tasks.filter(t => t.goalId === selectedGoalForSteps?.id && !t.stepId).map(task => (
                                <button
                                  key={task.id}
                                  onClick={() => {
                                    handleAttachTaskToStep(task.id, step.id);
                                    setExpandedStepOnCard(null);
                                  }}
                                  style={{
                                    padding: '8px',
                                    background: 'white',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    textAlign: 'left',
                                    color: '#374151'
                                  }}
                                >
                                  🎯 {task.name}
                                </button>
                              ))
                            )}
                          </div>
                          <button
                            onClick={() => setExpandedStepOnCard(null)}
                            style={{
                              marginTop: '8px',
                              padding: '6px 12px',
                              background: '#f3f4f6',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              width: '100%'
                            }}
                          >
                            Отмена
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setExpandedStepOnCard(step.id)}
                          style={{
                            marginTop: '10px',
                            padding: '6px 12px',
                            background: '#eff6ff',
                            color: '#3b82f6',
                            border: '1px solid #3b82f6',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            width: '100%'
                          }}
                        >
                          + Привязать задачу
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                  Нет шагов. Добавьте первый шаг выше.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модалка управления категориями */}
      {showCategoryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0 }}>📁 Управление категориями</h3>
            
            {/* Форма создания категории */}
            <form onSubmit={handleCreateCategory} style={{
              background: '#f9fafb',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '10px' }}>Новая категория</h4>
              <input
                type="text"
                placeholder="Название категории"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                required
                style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="text"
                  placeholder="Эмодзи (🎯)"
                  value={categoryForm.icon}
                  onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                  maxLength={2}
                  style={{ width: '80px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
                <input
                  type="color"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  style={{ width: '60px', height: '36px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                />
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '8px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Создать
                </button>
              </div>
            </form>

            {/* Список категорий */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px' }}>Существующие категории</h4>
              {categories.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                  Категорий пока нет. Создайте первую!
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {categories
                    .sort((a, b) => a.order - b.order)
                    .map((cat, index) => (
                      <div key={cat.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px',
                        background: '#f9fafb',
                        border: `2px solid ${cat.color}`,
                        borderRadius: '8px'
                      }}>
                        <div style={{
                          fontSize: '24px',
                          width: '30px',
                          textAlign: 'center'
                        }}>
                          {cat.icon}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold' }}>{cat.name}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            Порядок: {cat.order}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          {index > 0 && (
                            <button
                              onClick={() => handleUpdateCategoryOrder(cat.id, cat.order - 1)}
                              style={{
                                padding: '5px 10px',
                                background: '#f3f4f6',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                              title="Выше"
                            >
                              ↑
                            </button>
                          )}
                          {index < categories.length - 1 && (
                            <button
                              onClick={() => handleUpdateCategoryOrder(cat.id, cat.order + 1)}
                              style={{
                                padding: '5px 10px',
                                background: '#f3f4f6',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                              title="Ниже"
                            >
                              ↓
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteCategory(cat.id)}
                            style={{
                              padding: '5px 10px',
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: '1px solid #fecaca',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setShowCategoryModal(false)}
              style={{
                padding: '10px 20px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Закрыть
            </button>
          </div>
        </div>
      )}
      </div>
    </DndContext>
  );
};

export default GoalsTab;
